import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { API, graphqlOperation } from 'aws-amplify'
import Button from 'antd/lib/button'
import Input from 'antd/lib/input'
import Select from 'antd/lib/select'
import { updateUser } from '../redux/app/actions'
import { capitalizeEachWord, validateExtraField } from '../lib/stringOperations'
import Overlay from './Overlay'
import { trimD1 } from './Admin/Users/Details/API'
import PhoneInput from 'react-phone-number-input'
import { validatePhoneNumber } from './Auth/helpers'
import { useGetRegistrationSettings } from './Admin/Hooks/useGetRegistrationSettings'
import { formatPhoneNumber } from '../lib/helpers'

const Option = Select.Option

const styles = {
	container: {
		padding: '1em',
		overflow: 'scroll',
		maxHeight: 660,
		width: 505
	},
	button: {
		backgroundColor: '#1BAAF1',
		borderColor: '#1BAAF1',
	},
	splitColumns: {
		marginTop: '0.64em',
		display: 'flex',
		justifyContent: 'space-between',
		width: '100%',
	},
	buttonContainer: {
		marginTop: '1em',
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
	},
	infoContainer: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center'
	},
	submitButton: {
		width: 180,
		height: 40,
		borderRadius: 7,
	},
	required: {
		color: 'red',
		marginRight: 5,
	},
	error: {
		border: '1px solid red',
	},
	errorText: {
		display: 'block',
		color: 'red',
		marginTop: '0.64em'
	}
}

const updateOccupant = `mutation updateOccupant($pk: ID!, $sk: String!, $version: Int!,  $input: updateOccupantInput) {
    updateOccupant(pk: $pk, sk: $sk, expectedVersion: $version, input: $input) {
        pk
        sk
        d1
        d2
        firstName
        lastName
        email
        lastLogin
        version
        primaryContact
        roles
        metaData {
            createdAt
        }
        phone {
            number
        }
        workPhone {
            number
            extension
        }
        extraFields {
            key
            value
        }
        version
    }
}`

const UserConfigurator = () => {

	const user = useSelector(state => state.app.user)
	const tenant = useSelector(state => state.app.tenant)
	const building = useSelector(state => state.app.building)

	const { data: registrationSettings, loading: loadingUserFields } = useGetRegistrationSettings(building?.pk)

	const [formValues, setFormValues] = useState({
		email: user.email,
		firstName: capitalizeEachWord(user.firstName),
		lastName: capitalizeEachWord(user.lastName),
		floor: '',
		suite: '',
		workPhone: '',
		extension: '',
		phone: '',
		extraFields: {},
	})

	const suitesEnabled = building.settings.systemConfig.suites

	const [loading, setLoading] = useState(false)
	const [errorFields, setErrorFields] = useState({})
	const [errorMessage, setErrorMessage] = useState([])
	const [showForm, setShowForm] = useState(false)

	useEffect(() => {
		if (user.d1) {
			if (!user.haveLoggedIn && !loadingUserFields) {

				let obj = {}
				if (registrationSettings?.userFields) {
					registrationSettings.userFields.forEach(field => {
						obj[field.label] = user.extraFields?.find(f => f.key === field.label)?.value
					})
				}

				setFormValues((prevValues) => ({
					...prevValues,
					floor: user.d1.split("#")[5],
					suite: user.d1.split("#")[6],
					phone: formatPhoneNumber(user.phone?.number),
					workPhone: formatPhoneNumber(user.workPhone?.number),
					extension: user.workPhone?.extension,
					extraFields: obj
				}))

				setShowForm(true)
			}
		}
	}, [user, loadingUserFields, registrationSettings])

	useEffect(() => {
		if (Object.keys(errorFields).length > 0) {
			const msgArray = []
			for (const key in errorFields) {
				if (typeof errorFields[key] === 'string') {
					msgArray.push(`${key}: ${errorFields[key]}`);
				} else if (typeof errorFields[key] === 'boolean') {
					msgArray.push(`Please fill out all required fields`);
				}
			}
			setErrorMessage(msgArray);
		} else {
			setErrorMessage([])
		}

	}, [errorFields])

	const handleChange = (e) => {
		const { name, value } = e.target
		setFormValues((prevValues) => ({
			...prevValues,
			[name]: value,
		}))
	}

	const handleSelectChange = (name, value) => {
		setFormValues((prevValues) => ({
			...prevValues,
			[name]: value,
		}))
	}

	const onExtraFieldsChange = (name, value) => {
		setFormValues((prevValues) => ({
			...prevValues,
			extraFields: {
				...prevValues.extraFields,
				[name]: value,
			},
		}))
	}


	if (!showForm) return null

	const validateFields = () => {
		const errors = {}

		if (!formValues.firstName) errors.firstName = true
		if (!formValues.lastName) errors.lastName = true
		if (!formValues.floor) errors.floor = true
		if (suitesEnabled && tenant.suites?.length > 0 && !formValues.suite) errors.suite = true

		if (!validatePhoneNumber(formValues.workPhone, registrationSettings?.workPhone)) errors.workPhone = "Input Valid Phone number"
		if (!validatePhoneNumber(formValues.phone, registrationSettings?.phone)) errors.phone = "Input Valid Phone number"

		if (registrationSettings?.userFields) {
			registrationSettings.userFields.forEach((field) => {
				const value = formValues.extraFields[field.label]
				if (field) {
					const isValid = validateExtraField(
						value,
						field.required,
						field.advanced.numerical,
						field.advanced.minLength
					)
					if (!isValid) errors[field.label] = generateExtraFieldToolTip(field)
				}
			})
		}

		setErrorFields(errors)
		return Object.keys(errors).length > 0
	}

	const generateExtraFieldToolTip = (field) => {
		let str = ''

		// if (field.advanced.unique) {
		// 	str = 'Unique, '
		// }

		if (field.advanced.numerical) {
			str = str + 'Only Numbers.'
		}

		if (field.advanced.minLength) {
			str = str + ` A minimum of ${field.advanced.minLength} characters.`
		}

		return str ? str : false
	}

	const saveUser = async () => {

		if (!validateFields()) {
			setLoading(true)
			setErrorMessage([])

			let data = {
				pk: user.pk,
				sk: user.sk,
				version: user.version || 1,
				input: {
					haveLoggedIn: true,
					firstName: formValues.firstName.toLowerCase(),
					lastName: formValues.lastName.toLowerCase(),
					d1: `${trimD1(user.d1, 5)}#${formValues.floor}#${formValues.suite}`,
				}
			}
			if (formValues.phone) {
				data.input.phone = {
					number: formValues.phone,
				}
			}

			if (formValues.workPhone) {
				data.input.workPhone = {
					number: formValues.workPhone,
					extension: formValues.extension
				}
			}

			if (formValues.extraFields) {
				data.input.extraFields = Object.keys(formValues.extraFields).map(key => (
					{
						key: key,
						value: formValues.extraFields[key]
					}
				))
			}

			try {
				let response = await API.graphql(graphqlOperation(updateOccupant, data))
				updateUser(response.data.updateOccupant)
				setShowForm(false)
			} catch (e) {
				console.log('error: ', e)
				throw Error(e)
			}
		}
	}

	return (
		<Overlay
			onClose={() => setErrorMessage('Please fill out all required fields')}
			visible={showForm}
			minimal={true}
			closebutton={false}
		>
			<div style={styles.container}>
				<div style={{ textAlign: 'center' }}>
					<div style={styles.infoContainer}>
						<b>{building.name}</b>
						<span style={{ color: '#606060' }}>{tenant.name}</span>
					</div>
					<div style={{ marginTop: '0.64em', color: '#1890FF' }}>
						CONFIRM USER INFORMATION
					</div>
				</div>
				<div style={styles.errorText}>
					<ul>
						{errorMessage.map((msg, index) => (
							<li key={index}>{msg}</li>
						))}
					</ul>
				</div>
				<div style={{ marginTop: '1em' }}>
					<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
						<div style={{ width: 200 }}>
							<span style={styles.required}>*</span>
							<span style={{ width: 150 }}>First Name:</span>
						</div>
					</div>
					<Input
						name="firstName"
						style={errorFields.firstName ? styles.error : null}
						onChange={handleChange}
						value={formValues.firstName}
					/>
				</div>
				<div style={{ marginTop: '1em' }}>
					<span>
						<span style={styles.required}>*</span>
						Last Name:
					</span>
					<Input
						name="lastName"
						style={errorFields.lastName ? styles.error : null}
						onChange={handleChange}
						value={formValues.lastName}
					/>
				</div>
				<div style={styles.splitColumns}>
					<div style={suitesEnabled && tenant.suites ? { width: '48%' } : { width: '100%' }}>
						<span>
							<span style={styles.required}>*</span>
							Floor:
						</span>
						<Select
							name="floor"
							style={errorFields.floor ? { ...styles.error, width: '100%' } : { width: '100%' }}
							value={formValues.floor}
							onChange={(value) => handleSelectChange('floor', value)}
						>
							{tenant.floors.map((floor) => (
								<Option key={floor} value={floor}>
									{floor}
								</Option>
							))}
						</Select>
					</div>
					{suitesEnabled && tenant.suites && <div style={{ width: '48%' }}>
						<span>
							<span style={styles.required}>*</span>
							Suite:
						</span>
						<Select
							name="suite"
							style={errorFields.suite ? { ...styles.error, width: '100%' } : { width: '100%' }}
							value={formValues.suite}
							onChange={(value) => handleSelectChange('suite', value)}
						>
							{tenant.suites.map((suite) => (
								<Option key={suite} value={suite}>
									{suite}
								</Option>
							))}
						</Select>
					</div>}
				</div>
				<div style={styles.splitColumns}>
					<div style={{ width: '68%' }}>
						<span>
							{registrationSettings?.workPhone && <span style={styles.required}>*</span>}
							Work Phone:
						</span>
						<PhoneInput
							name="workPhone"
							style={errorFields.workPhone ? styles.error : null}
							onChange={(value) => handleSelectChange('workPhone', value)}
							defaultCountry="US"
							value={formValues.workPhone}
						/>
					</div>
					<div style={{ width: '28%' }}>
						<span>Ext:</span>
						<Input
							name="extension"
							onChange={handleChange}
							value={formValues.extension}
						/>
					</div>
				</div>
				<div style={{ marginTop: '1em', width: '68%' }}>
					<span>
						{registrationSettings?.phone && <span style={styles.required}>*</span>}
						Mobile Phone:
					</span>
					<PhoneInput
						name="phone"
						style={errorFields.phone ? styles.error : null}
						onChange={(value) => handleSelectChange('phone', value)}
						defaultCountry="US"
						value={formValues.phone}
					/>
				</div>

				{registrationSettings && registrationSettings.userFields?.map((field) => (
					<div key={field.label} style={{ marginTop: '1em' }}>
						<span>
							{field.required && <span style={styles.required}>*</span>}
							{field.label}:
						</span>
						<Input
							name={field.label}
							style={errorFields[field.label] ? styles.error : null}
							placeholder={`Input ${field.label}`}
							value={formValues.extraFields[field.label] || ''}
							onChange={(e) => onExtraFieldsChange(field.label, e.target.value)}
						/>
					</div>
				))}
				<div style={styles.buttonContainer}>
					<Button style={{ margin: 'auto', display: 'block' }} onClick={saveUser}
						type="primary">Update</Button>
				</div>
			</div>
		</Overlay>
	)
}


export default UserConfigurator